import { useState, useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { ReactComponent as ExportIcon } from "assets/images/icons/TopNavIcons/ExportIcon.svg";
import { toast } from "react-toastify";
// import { useForm, FormProvider } from "react-hook-form";
import useTableRequest from "hooks/useTableRequest";
import DataTable from "components/organisms/DataTable/DataTable";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import Badge from "components/atoms/Badge/Badge";
import { getTeamMemberList, exportDetail, deactivateAccount, reactivateAccount } from "api";
import Button from "components/atoms/Button/Button";
import useConfirm from "hooks/useConfirm";
import usePermissions from "hooks/usePermissions";
import UserDialog from "./UserDialog";

interface CellInterface {
  value: string;
}
const Users = function () {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [downloading, setDownloading] = useState(false);
  const [category, setCategory] = useState("active,pending");
  const [showDialog, setShowDialog] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [selectedUserStatus, setSelectedUserStatus] = useState("");
  const { permissions } = usePermissions();
  const { confirm, ConfirmModal } = useConfirm();
  const adminSettings = JSON.parse(permissions || "{}")?.permissions?.admin_settings;
  const viewOnly = Boolean(!adminSettings?.edit_users);

  const columns = useMemo(
    () => [
      {
        Header: (
          <span className="pl-9 ">
            <FormattedMessage id="general.name" defaultMessage="NAME" />
          </span>
        ),
        sortable: true,
        accessor: "name", // accessor is the "key" in the data
        Cell: ({ value }: CellInterface) => <p className="font-weight-bold pl-9 pt-8 pb-8 mb-0">{value}</p>,
      },
      {
        Header: (
          <span className="mb-0">
            <FormattedMessage id="settings.team-members.tableRoleHeader" defaultMessage="Role" />
          </span>
        ),
        sortable: true,
        accessor: "role",
        Cell: ({ value }: CellInterface) => <div className="pt-8 pb-8">{value}</div>,
      },
      {
        Header: (
          <span className="mb-0">
            <FormattedMessage id="general.email" defaultMessage="Email" />
          </span>
        ),
        sortable: true,
        accessor: "email",
        Cell: ({ value }: CellInterface) => <div className="pt-8 pb-8">{value}</div>,
      },
      {
        Header: (
          <span className="mb-0">
            <FormattedMessage id="general.teams" defaultMessage="Teams" />
          </span>
        ),
        sortable: true,
        accessor: "provider_team",
        Cell: ({ value }: CellInterface) => <div className="pt-8 pb-8 overflow-hidden text-truncate w-200-px">{value}</div>,
      },
      {
        Header: (
          <span className="mb-0 text-right ">
            <FormattedMessage id="general.status" defaultMessage="Status" />
          </span>
        ),
        accessor: "status",
        sortable: true,
        Cell: ({ value }: CellInterface) => {
          const badgeType = (value: string) => {
            if (value === "Active") return "primary";
            if (value === "Pending") return "warning";
            return "secondary";
          };
          return (
            <div className="d-flex">
              <Badge type={badgeType(value)} label={value} />
            </div>
          );
        },
      },
    ],
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = () => {
    setDownloading(true);
    const config = { responseType: "blob" };
    exportDetail(config).then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setDownloading(false);
    });
  };

  const { instance, fetching, totalData, refetch } = useTableRequest(getTeamMemberList, columns, {
    params: {},
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      status: category,
    }),
    deps: [category],
  });

  const handleDeactivateAccount = async (data: { userName: string; companyName: string; email: string }) => {
    const response = await confirm({
      title: intl.formatMessage({ id: "settings.team-members.deactivateAccount", defaultMessage: "Deactivate account" }),
      message: intl.formatMessage(
        {
          id: "settings.user.deactivateUserWarning",
          defaultMessage: "Are you sure that you want to deactivate this account?",
        },
        { name: data.userName, company_name: data.companyName },
      ),
    });
    if (response) {
      deactivateAccount(data.email)
        .then(() => {
          refetch();
          toast(
            intl.formatMessage({
              id: "settings.deleteTeamMemberSuccessMessage",
              defaultMessage: "User account deactivated successfully",
            }),
            { type: "success" },
          );
        })
        .catch((error) => toast(error, { type: "error" }));
    }
  };

  const handleReactivateAccount = async (data: { userName: string; companyName: string; email: string }) => {
    const response = await confirm({
      title: intl.formatMessage({ id: "settings.team-members.reactivateAccount", defaultMessage: "Reactivate account" }),
      message: intl.formatMessage(
        {
          id: "settings.user.reActivateUserWarning",
          defaultMessage: "Are you sure that you want to reactivate this account?",
        },
        { name: data.userName, company_name: data.companyName },
      ),
    });
    if (response) {
      reactivateAccount(data.email)
        .then(() => {
          refetch();
          toast(
            intl.formatMessage({
              id: "settings.activateTeamMemberSuccessMessage",
              defaultMessage: "User account reactivated successfully",
            }),
            { type: "success" },
          );
        })
        .catch((error) => toast(error, { type: "error" }));
    }
  };

  return (
    <>
      <ConfirmModal />
      <UserDialog
        viewOnly={viewOnly}
        refetch={refetch}
        show={showDialog}
        userStatus={selectedUserStatus}
        onDeactivateAccount={handleDeactivateAccount}
        onReactivateAccount={handleReactivateAccount}
        setUserEmail={setUserEmail}
        userEmail={userEmail}
        close={() => setShowDialog(false)}
      />
      <DataTable
        tableInstance={instance}
        totalData={totalData}
        isLoading={fetching}
        draggable={false}
        onRowClick={(row) => {
          setUserEmail(row.original.email);
          setSelectedUserStatus(row.original.status);
          setShowDialog(true);
        }}
        headerLeft={
          <div className="d-flex align-items-center mt-3">
            <PillTabs
              id="quote-statuses"
              activeKey={category}
              onSelect={(eventKey: any) => {
                searchParams.set("page", "1");
                setSearchParams(searchParams);
                setCategory(eventKey);
              }}
              tabItems={[
                {
                  eventKey: "active,pending",
                  title: intl.formatMessage({
                    id: "settings.team-members.activePendingFilter",
                    defaultMessage: "Active/Pending",
                  }),
                },
                {
                  eventKey: "inactive",
                  title: intl.formatMessage({ id: "settings.team-members.inactiveFilter", defaultMessage: "Inactive" }),
                },
                {
                  eventKey: "",
                  title: intl.formatMessage({
                    id: "settings.team-members.allFilter",
                    defaultMessage: "All accounts",
                  }),
                },
              ]}
            />
            <GlobalTableSearch
              currentFilters={instance.state.globalFilter}
              setFilters={instance.setGlobalFilter}
              filterKey="query"
              eventContext="Team members"
            />
          </div>
        }
        headerRight={
          <div className="d-flex align-items-center mt-3">
            {!downloading ? (
              <OverlayTrigger placement="bottom" overlay={<Tooltip id="test">Export users overview</Tooltip>}>
                <ExportIcon onClick={handleDownload} className="cursor-pointer" />
              </OverlayTrigger>
            ) : (
              <div className="spinner-border spinner-border-sm" role="status" />
            )}
            {!viewOnly && (
              <Button
                label={intl.formatMessage({
                  id: "settings.user.addUser",
                  defaultMessage: "Add user",
                })}
                className="rounded-pill ml-3"
                style={{ height: "26px" }}
                onClick={() => setShowDialog(true)}
              />
            )}
          </div>
        }
      />
    </>
  );
};
Users.defaultProps = {
  globalFilter: "",
  setGlobalFilter: null,
  value: "",
};
export default Users;
